import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Color, Size, t, pageKindLineText } from '@bms2/shared';
import { Button, DatePicker, Pill, TextInput } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { PageModule } from '../../shell/PageModule';
import type { TodoRow, TodosAnswer } from '../../shell/AppShell';
import { post, useApi } from '../records/api';
import styles from './TodosPage.module.css';
import { useNotice } from '../../components/controls/useNotice';

/**
 * The To-Do page at /todos/role (piece 9; decision 0344): the viewer's open
 * `ChecklistItem` rows, their own and their role's, soonest due first. 🔨 Done
 * is offered only where the kind closes on the person's own tick; a row that
 * closes on a record links to that record instead. Snooze moves the due date
 * and writes nothing else; Dismiss asks the reason the plan requires.
 */
export const TODOS_PATH = '/todos/mine';
export const CHECKLIST_ITEMS_PATH = '/checklist-items';

type Pending = { id: string; kind: 'snooze'; until: string } | { id: string; kind: 'dismiss'; reason: string } | null;

const PRIORITY_TONE: Record<string, { fg: string; bg: string } | null> = {
  Urgent: { fg: 'var(--status-error-text)', bg: 'var(--status-error-bg)' },
  High: { fg: 'var(--status-warning-text)', bg: 'var(--status-warning-bg)' },
  Normal: null,
  Low: null,
};

export function TodosPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const fetched = useApi<TodosAnswer>(TODOS_PATH, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [pending, setPending] = useState<Pending>(null);
  const rows = useMemo(() => (fetched.state.kind === 'ready' ? fetched.state.data.rows : []), [fetched.state]);
  const mine = rows.filter((row) => row.mine);
  const roles = rows.filter((row) => !row.mine);

  const act = async (row: TodoRow, action: 'complete' | 'snooze' | 'dismiss', body: Record<string, unknown>, done: string) => {
    const answer = await post(`${CHECKLIST_ITEMS_PATH}/${row.id}/${action}`, body, apiBaseUrl);
    setNotice(answer.ok ? { tone: 'good', text: `${done} · ${row.title}` } : { tone: 'error', text: answer.message });
    if (answer.ok) {
      setPending(null);
      fetched.reload();
    }
  };

  const item = (row: TodoRow) => {
    const tone = PRIORITY_TONE[row.priority] ?? null;
    const open = pending && pending.id === row.id ? pending : null;
    return (
      <li key={row.id} className={styles.item} data-todo={row.id} data-todo-kind={row.kind} data-todo-closes-on-tick={row.closesOnTick ? 'true' : 'false'}>
        <div className={styles.main}>
          <span className={styles.title}>
            {row.title}
            {tone ? <Pill text={row.priority} foreground={tone.fg} background={tone.bg} /> : null}
          </span>
          {row.description ? <span className={styles.meta}>{row.description}</span> : null}
          <span className={styles.meta} data-todo-due={row.dueDate ?? ''}>
            {row.dueDate ? `${t('todos.due')} ${row.dueDate}` : t('todos.noDue')}
            {row.closesOnTick ? null : ` · ${t('todos.closesOnRecord')}`}
          </span>
          {row.route ? <Link to={row.route} className={styles.link} data-todo-open="true">{t('todos.openRecord')}</Link> : null}
        </div>
        <div className={styles.actions}>
          {row.closesOnTick ? <Button size={Size.Small} color={Color.Navy} text={[t('todos.done')]} actions={[() => void act(row, 'complete', {}, t('todos.completed'))]} /> : null}
          <Button size={Size.Small} color={Color.Inactive} text={[t('todos.snooze')]} actions={[() => setPending({ id: row.id, kind: 'snooze', until: '' })]} />
          <Button size={Size.Small} color={Color.Inactive} text={[t('todos.dismiss')]} actions={[() => setPending({ id: row.id, kind: 'dismiss', reason: '' })]} />
        </div>
        {open?.kind === 'snooze' ? (
          <div className={styles.inline} data-todo-snooze="true">
            <DatePicker size={Size.Small} label={t('todos.snoozeUntil')} value={open.until} onChange={(until) => setPending({ id: row.id, kind: 'snooze', until })} required />
            <Button size={Size.Small} color={Color.Navy} text={[t('todos.snooze')]} disabled={open.until === ''} actions={[() => void act(row, 'snooze', { until: open.until }, t('todos.snoozed'))]} />
          </div>
        ) : null}
        {open?.kind === 'dismiss' ? (
          <div className={styles.inline} data-todo-dismiss="true">
            <TextInput size={Size.Large} label={t('todos.dismissReason')} value={open.reason} onChange={(reason) => setPending({ id: row.id, kind: 'dismiss', reason })} required />
            <Button size={Size.Small} color={Color.Navy} text={[t('todos.dismiss')]} disabled={open.reason.trim() === ''} actions={[() => void act(row, 'dismiss', { reason: open.reason.trim() }, t('todos.dismissed'))]} />
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <PageModule title={t('todos.title')} subtitle={pageKindLineText({ page: 'named', route: '/todos' })}>
      <div className={styles.page} data-page="todos">
        {noticeBanner}
        <AsyncStatePanel state={fetched.state} emptyAction={t('todos.empty')} onRetry={fetched.reload}>
          {(answer) => (
            <div className={styles.sections} data-todos-count={answer.count}>
              {answer.rows.length === 0 ? <p className={styles.meta}>{t('todos.empty')}</p> : null}
              {mine.length > 0 ? (
                <section className={styles.section} data-todos-section="mine">
                  <h2 className={styles.heading}>{t('todos.mine')}</h2>
                  <ul className={styles.list}>{mine.map(item)}</ul>
                </section>
              ) : null}
              {roles.length > 0 ? (
                <section className={styles.section} data-todos-section="role">
                  <h2 className={styles.heading}>{t('todos.role')}</h2>
                  <ul className={styles.list}>{roles.map(item)}</ul>
                </section>
              ) : null}
            </div>
          )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}
